import { Typography } from "@mui/material";
import { keyBy } from "lodash";
import { ReactElement } from "react";

import { ListAllActivePricingAssignmentsQuery } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink } from "~frontend/components/Link";
import Table from "~frontend/components/Table";
import { TableBox } from "~frontend/components/Table";
import TagChip from "~frontend/components/TagChip";
import { useTenantSlug } from "~frontend/providers/TenantSlugProvider";
import { palette } from "~frontend/theme";
import { getTenantHref } from "~frontend/util/tenantUrl";

type TenantData =
  ListAllActivePricingAssignmentsQuery["listAllActivePricingAssignments"]["tenants"][number];
type SubaccountData = TenantData["subaccountAssignments"][number];

type TenantRow = TenantData & {
  kind: "tenant";
  subaccountAssignments: SubaccountRow[];
};
type SubaccountRow = SubaccountData & { kind: "subaccount" };
type PricingAssignmentRow = TenantRow | SubaccountRow;

function toRows(tenants: TenantData[]): TenantRow[] {
  const byTenantId = keyBy(tenants, t => t.tenantId);
  return Object.values(byTenantId).map(tenant => {
    const bySubaccountId = keyBy(
      tenant.subaccountAssignments,
      sa => sa.subaccountId
    );
    return {
      ...tenant,
      kind: "tenant" as const,
      subaccountAssignments: Object.values(bySubaccountId).map(sa => ({
        ...sa,
        kind: "subaccount" as const,
      })),
    };
  });
}

function getChildRows(row: PricingAssignmentRow): PricingAssignmentRow[] {
  if (row.kind === "tenant") {
    return row.subaccountAssignments;
  }
  return [];
}

export default function PricingAssignmentsTable({
  tenants,
  totalCount,
  page,
  pageSize,
  onChangePage,
  onChangePageSize,
}: {
  tenants: TenantData[];
  totalCount: number;
  page: number;
  pageSize: number;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
}): ReactElement {
  const currentTenantSlug = useTenantSlug();
  const rows = toRows(tenants);

  return (
    <TableBox>
      <Table<PricingAssignmentRow>
        columns={[
          {
            key: "tenant",
            title: "Tenant",
            renderValue: row => {
              if (row.kind === "tenant") {
                return (
                  <ExternalLink
                    href={getTenantHref(currentTenantSlug, row.tenantSlug)}
                  >
                    <Typography variant="body2">{row.tenantName}</Typography>
                  </ExternalLink>
                );
              }
              return (
                <FlexBox gap={1} alignItems="center">
                  {row.isWeText && (
                    <TagChip
                      label="WeText"
                      sx={{
                        borderRadius: 0,
                        fontWeight: 600,
                        color: palette.navy,
                        fontSize: "10px",
                      }}
                    />
                  )}
                  <Typography variant="body3">{row.subaccountName}</Typography>
                </FlexBox>
              );
            },
          },
          {
            key: "pricingOption",
            title: "Pricing Option",
            renderValue: row => row.pricingBundleName,
          },
        ]}
        rows={rows}
        getChildRows={getChildRows}
        totalCount={totalCount}
        rowsPerPage={pageSize}
        onChangeRowsPerPage={newSize => {
          onChangePageSize(newSize);
          onChangePage(0);
        }}
        page={page}
        onChangePage={onChangePage}
        emptyState="No active pricing assignments found"
      />
    </TableBox>
  );
}
